<template>
  <div>
    <div
      v-if="isMainOrder && orderDetail.status === 'active'"
      class="d-flex align-center">
      <v-tabs
        v-model="warehouseSelected"
        class="flex-grow-1"
        style="min-width: 0;">
        <v-tab
          v-for="(tab, index) in tabHeaders"
          :key="`tabs-index-${index}`">
          {{ tab.name }}
        </v-tab>
      </v-tabs>
      <ac2-wms-status-button
        v-if="selectedSubOrderId"
        class="mx-2 flex-shrink-0"
        :synced="selectedSubOrderSynced"
        :synced-at="selectedSubOrderAc2Sync && selectedSubOrderAc2Sync.updatedAt"
        :loading="!!ac2Syncing[selectedSubOrderId]"
        :readonly="!selectedSubOrderSyncable"
        :readonly-text="`(sync available only when ${ac2SyncableState.split('_').join(' ')})`"
        @click="confirmAc2Sync(selectedSubOrderId)" />
    </div>
    <v-tabs-items
      v-if="isMainOrder && orderDetail.status === 'active'"
      v-model="warehouseSelected"
      touchless>
      <v-divider></v-divider>
      <v-tab-item
        v-for="(tab, tabIndex) in tabHeaders"
        :key="`tab-items-index-${tabIndex}`"
        :value="tabIndex">
        <v-data-table
          id="order-product-table"
          class="pa-0"
          :class="{ 'order-product-empty': !hasOrderSkus }"
          :headers="tableHeader"
          :items="allSubOrders[tab.id]"
          :items-per-page="-1"
          :mobile-breakpoint="10"
          fixed-header
          hide-default-footer
          :height="getHeightTable">
          <template #[`item.images`]="{ item }">
            <gw-product-image
              class="order-product-image"
              :src="imgSrcCompute($convertImgix(item.images[0]))" />
            <div
              v-if="getPreOrder(item)"
              class="d-flex justify-center mt-1">
              <v-chip
                color="orange"
                dark
                x-small>
                Pre-Order
              </v-chip>
            </div>
          </template>
          <template #[`item.name`]="{ item }">
            <div class="order-product-name">
              <p class="mb-0 font-super-bold">
                {{ item.name }}
              </p>
              <p class="mb-0">
                <b>Model : </b>{{ item.code }}
              </p>
              <p class="mb-0">
                <b>Option : </b>{{ item.color }} - {{ item.size }}
              </p>
              <p
                v-if="!isMain(tab.id) && item.trackingNo"
                class="mb-0">
                <b>Tracking no. : </b>{{ item.trackingNo }}
              </p>
            </div>
          </template>
          <template #[`item.amount`]="{ item }">
            <v-text-field
              v-model.number="item.amount"
              disabled
              class="amount-input order-summary-input"
              type="number"
              min="1"
              dense
              solo
              outlined
              hide-details
              @input="calcSkuPrice({sku: item, amount: Number($event), field: 'amount'})" />
          </template>

          <template #[`item.price`]="{ item }">
            <p class="text-price">
              {{ getUnitPrice(item) | showFullPriceFormat() }}
            </p>
          </template>

          <template #[`item.discount`]="{ item }">
            <div class="d-flex">
              <v-text-field
                v-model.number="item.discountPrice"
                :disabled="inputDisabled || !editMode || !isMain(tab.id)"
                class="discount-input order-summary-input"
                dense
                solo
                outlined
                hide-details
                @keypress="$keypressNumber($event)"
                @input="calcSkuPrice({ sku: item })" />
              <v-select
                v-model="item.discountType"
                :items="discountType"
                :disabled="inputDisabled || !editMode || !isMain(tab.id)"
                :menu-props="{
                  offsetY: true,
                  contentClass: 'order-summary-select-menu'
                }"
                item-text="label"
                item-value="value"
                class="discount-select order-summary-select"
                hide-details
                dense
                outlined
                @change="calcSkuPrice({sku: item, discountType: $event})">
              </v-select>
            </div>
          </template>
          <template #[`item.net`]="{ item }">
            <p class="text-price">
              {{ item.price | showFullPriceFormat() }}
            </p>
          </template>
          <template #no-data>
            <v-btn
              :disabled="!editMode"
              color="#ffffff"
              icon
              block
              @click="openProductModal()">
              <gw-svg-icon
                svg-name="cart-plus-solid"
                width="75"
                heigth="75"
                color="#a4a4a4" />
            </v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>
    <v-data-table
      v-else
      id="order-product-table"
      class="pa-0"
      :class="{ 'order-product-empty': !hasOrderSkus }"
      :headers="tableHeader"
      :items="skus"
      :items-per-page="-1"
      :mobile-breakpoint="10"
      :height="getHeightTable"
      fixed-header
      hide-default-footer>
      <template #[`item.images`]="{ item }">
        <gw-product-image
          class="order-product-image"
          :src="imgSrcCompute($convertImgix(item.images[0]))" />
        <div
          v-if="getPreOrder(item)"
          class="d-flex justify-center mt-1">
          <v-chip
            color="orange"
            dark
            x-small>
            Pre-Order
          </v-chip>
        </div>
      </template>
      <template #[`item.name`]="{ item }">
        <div class="order-product-name">
          <p class="mb-0 font-super-bold">
            {{ item.name }}
          </p>
          <p class="mb-0">
            <b>Model : </b>{{ item.code }}
          </p>
          <p class="mb-0">
            <b>Color : </b>{{ item.color }}
          </p>
          <p class="mb-0">
            <b>Size : </b>{{ item.size }}
          </p>
          <p
            v-if="shippingCode"
            class="mb-0">
            <b>Tracking no. : </b>{{ shippingCode }}
          </p>
        </div>
      </template>
      <template #[`item.amount`]="{ item }">
        <v-text-field
          v-model.number="item.amount"
          class="amount-input order-summary-input"
          disabled
          min="1"
          dense
          solo
          outlined
          hide-details />
      </template>

      <template #[`item.price`]="{ item }">
        <p class="text-price">
          {{ item.unitPrice | showFullPriceFormat() }}
        </p>
      </template>

      <template #[`item.discount`]="{ item }">
        <div class="d-flex">
          <v-text-field
            v-model.number="item.discountPrice"
            disabled
            class="discount-input order-summary-input"
            dense
            solo
            outlined
            hide-details
            @keypress="$keypressNumber($event)"
            @input="calcSkuPrice({ sku: item })" />
          <v-select
            v-model="item.discountType"
            :items="discountType"
            disabled
            :menu-props="{
              offsetY: true,
              contentClass: 'order-summary-select-menu'
            }"
            item-text="label"
            item-value="value"
            class="discount-select order-summary-select"
            hide-details
            dense
            outlined>
          </v-select>
        </div>
      </template>
      <template #[`item.net`]="{ item }">
        <p class="text-price">
          {{ item.price | showFullPriceFormat() }}
        </p>
      </template>
      <template #no-data>
        <v-btn
          :disabled="!editMode"
          color="#ffffff"
          icon
          block
          @click="openProductModal()">
          <gw-svg-icon
            svg-name="cart-plus-solid"
            width="75"
            heigth="75"
            color="#a4a4a4" />
        </v-btn>
      </template>
    </v-data-table>

    <v-btn
      v-if="showButton"
      class="order-product-add-btn"
      :ripple="false"
      :elevation="0"
      @click="openProductModal()">
      <v-icon color="#898989">
        mdi-plus
      </v-icon>
      <span class="ml-2">
        {{ btnText }}
      </span>
    </v-btn>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getRole } from '@/assets/js/Authentication'
import OrderProvider from '@/resources/OrderProvider'
import Ac2WarehouseProvider from '@/resources/Ac2WarehouseProvider'
import Ac2WmsStatusButton from '@/components/Ac2WmsStatusButton.vue'

const OrderService = new OrderProvider()
const Ac2WarehouseService = new Ac2WarehouseProvider()

const AC2_NOT_SYNCABLE_STATES = [
  'prepare_create',
  'pending',
  'reserved',
  'waiting',
  'paid',
  'no_state',
  'kick',
  'void',
  'deleted'
]

// B2B ส่ง SO เข้า WMS ตั้งแต่ reserved (channel อื่นส่งตอน ready_to_ship) จึงโชว์สถานะ WMS ได้ตั้งแต่ reserved
const AC2_NOT_SYNCABLE_STATES_B2B = AC2_NOT_SYNCABLE_STATES.filter((state) => !['reserved', 'waiting', 'paid'].includes(state))

const B2B_CHANNELS = ['b2b_thb', 'b2b_usd']

export default {
  components: {
    Ac2WmsStatusButton
  },
  props: {
    inputDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      warehouseSelected: 'main',
      ac2Syncing: {},
      // warehouseId -> true/false (warehouse นี้อยู่ภายใต้ AC2 หรือไม่)
      ac2ManagedWarehouses: {},
      tableHeader: [
        {
          text: 'Product',
          align: 'left',
          value: 'images',
          width: 'auto',
          sort: false
        },
        {
          text: 'Info',
          align: 'left',
          value: 'name',
          width: 250,
          sortable: false
        },
        {
          text: 'QTY',
          align: 'center',
          value: 'amount',
          width: this.$vuetify.breakpoint.xsOnly ? 100 : 'auto',
          sortable: false
        },
        {
          text: 'Price',
          align: 'right',
          value: 'price',
          width: 'auto',
          sortable: false
        },
        {
          text: 'Discount',
          align: 'center',
          value: 'discount',
          width: this.$vuetify.breakpoint.xsOnly ? 300 : 150,
          sortable: false
        },
        {
          text: 'Amount',
          align: 'right',
          value: 'net',
          width: 100,
          sortable: false
        }
      ],
      discountType: [
        {
          label: '฿',
          value: 'amount'
        }
        // {
        //   label: '%',
        //   value: 'percent'
        // }
      ]
    }
  },
  computed: {
    ...mapGetters({
      skus: 'Order/orderSkus',
      hasOrderSkus: 'Order/hasOrderSkus',
      orderDetail: 'Order/orderDetail',
      isMainOrder: 'Order/isMainOrder',
      editMode: 'Order/onEditMode',
      allSubOrders: 'Order/allSubOrders'
    }),
    showButton () {
      const excludeChannels = ['b2b_thb', 'b2b_usd']

      return !this.inputDisabled && this.editMode && !excludeChannels.includes(this.orderDetail.channel)
    },
    getHeightTable () {
      if (this.$vuetify.breakpoint.xsOnly) return 'auto'

      const isEditProduct = this.inputDisabled ? 45 : 0
      const editHeight = this.editMode ? 47 : 0
      return (this.$vuetify.breakpoint.height - 350) + isEditProduct - editHeight
    },
    btnText () {
      return this.hasOrderSkus ? 'Edit Product' : 'Add Product'
    },
    tabHeaders () {
      const headers = [
        { name: 'main', id: 0 }
      ]
      const subOrders = this.orderDetail?.subOrders?.map((subOrder, index) => ({
        id: `${subOrder?.warehouse.id}${index}`,
        name: subOrder?.warehouse.name,
        warehouseId: subOrder?.warehouse.id,
        orderId: subOrder?.orderId || null,
        currentState: subOrder?.currentState || null,
        ac2Sync: subOrder?.ac2Sync || null
      })) || []
      return [...headers, ...subOrders]
    },
    selectedSubOrderId () {
      // ปุ่ม WMS เปิดให้เฉพาะ developer
      if (getRole() !== 'developer') {
        return null
      }

      // tab index 0 = main order, ตั้งแต่ 1 คือ sub-order (orderWarehouse) แต่ละ warehouse
      const tab = this.tabHeaders[this.warehouseSelected]

      if (!tab?.orderId || !this.ac2ManagedWarehouses[tab.warehouseId]) {
        return null
      }

      const notSyncableStates = this.isB2B ? AC2_NOT_SYNCABLE_STATES_B2B : AC2_NOT_SYNCABLE_STATES

      return notSyncableStates.includes(tab.currentState) ? null : tab.orderId
    },
    isB2B () {
      return B2B_CHANNELS.includes(this.orderDetail?.channel)
    },
    ac2SyncableState () {
      // ตรงกับ rule ฝั่ง backend: B2B sync ได้ตอน reserved, channel อื่นตอน ready_to_ship
      return this.isB2B ? 'reserved' : 'ready_to_ship'
    },
    selectedSubOrderSyncable () {
      // manual sync ใช้กรณี enqueue อัตโนมัติผิดพลาด จึงกดได้เฉพาะสถานะที่ส่ง SO เท่านั้น
      return this.tabHeaders[this.warehouseSelected]?.currentState === this.ac2SyncableState
    },
    selectedSubOrderAc2Sync () {
      return this.tabHeaders[this.warehouseSelected]?.ac2Sync || null
    },
    selectedSubOrderSynced () {
      return !!(this.selectedSubOrderAc2Sync?.sync && this.selectedSubOrderAc2Sync?.ac2Log)
    },
    shippingCode () {
      return this.orderDetail?.shipping?.refCode || ''
    },
    mainSkus () {
      return this.orderDetail?.skus?.reduce((arr, sku) => {
        const tmpArr = [...arr]
        const found = tmpArr.findIndex((mainSku) => mainSku.id === sku.id)

        if (found === -1) {
          tmpArr.push({
            ...sku,
            discountType: 'amount',
            discountPrice: Number((sku.fullPrice - sku.price).toFixed(3)),
            unitPrice: sku.fullPrice / sku.amount
          })
        } else {
          tmpArr[found] = {
            ...tmpArr[found],
            amount: tmpArr[found].amount + sku.amount,
            price: tmpArr[found].price + sku.price,
            discountPrice: Number((tmpArr[found].discountPrice + (sku.fullPrice - sku.price)).toFixed(3)),
            fullPrice: tmpArr[found].fullPrice + sku.fullPrice
          }

          tmpArr[found].unitPrice = tmpArr[found].fullPrice / tmpArr[found].amount
        }

        return tmpArr
      }, []) || []
    }
  },
  watch: {
    orderDetail () {
      this.initSubOrders()
      this.fetchAc2ManagedWarehouses()
    }
  },
  mounted () {
    this.initSubOrders()
    this.fetchAc2ManagedWarehouses()
  },
  methods: {
    ...mapActions({
      calcSkuPrice: 'Order/calcSkuPrice',
      setProductModalActive: 'Order/setProductModalActive',
      setAllSubOrders: 'Order/setAllSubOrders',
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    async fetchAc2ManagedWarehouses () {
      if (getRole() !== 'developer') {
        return
      }

      const subOrders = this.orderDetail?.subOrders || []
      const warehouseIds = [...new Set(subOrders.map((subOrder) => subOrder?.warehouse?.id).filter(Boolean))]
        .filter((id) => !(id in this.ac2ManagedWarehouses))

      await Promise.all(warehouseIds.map(async (warehouseId) => {
        try {
          const { data } = await Ac2WarehouseService.resolveAc2Warehouse(warehouseId)
          // resolve เจอ mapping active (data ไม่เป็น null) = warehouse นี้จัดการผ่าน WMS
          this.$set(this.ac2ManagedWarehouses, warehouseId, !!data)
        } catch (error) {
          console.error('fetchAc2ManagedWarehouses', error)
        }
      }))
    },
    confirmAc2Sync (orderId) {
      this.setModal({
        value: true,
        title: `Sync WMS ${orderId}`,
        message: `ต้องการส่ง ${orderId} เข้าคิว AC2 WMS ใช่หรือไม่ (ใช้กรณีส่งเข้าคิวอัตโนมัติไม่สำเร็จ)`,
        confirmText: 'Sync',
        confirmType: 'primary',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.syncAc2(orderId)
      })
    },
    async syncAc2 (orderId) {
      if (this.ac2Syncing[orderId] || !this.selectedSubOrderSyncable) {
        return
      }

      try {
        this.$set(this.ac2Syncing, orderId, true)

        const { data } = await OrderService.syncOrderWarehouseToAc2(orderId)

        this.setSnackbar({
          value: true,
          message: `ส่ง ${data?.documentNo || orderId} เข้าคิว Sync AC2 WMS สำเร็จ`,
          type: 'success'
        })
      } catch (error) {
        console.error('syncAc2', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.$set(this.ac2Syncing, orderId, false)
      }
    },
    isMain (id) {
      return id === 0
    },
    isAmountDiscount (type) {
      return type === 'amount'
    },
    getUnitPrice (sku) {
      return sku.unitPrice || sku.fullPrice / sku.amount
    },
    initSubOrders () {
      const rawSubOrders = this.orderDetail?.subOrders || []
      const subOrders = this.mapSubOrders(rawSubOrders)
      this.setAllSubOrders(subOrders)
    },
    mapSubOrders (subOrders) {
      const mapped = {}

      const mainSkusDiscount = this.mainSkus.reduce((obj, sku) => {
        const tmpObj = { ...obj }

        tmpObj[sku.id] = (tmpObj[sku.id] || 0) + sku.discountPrice

        return tmpObj
      }, {})

      subOrders.forEach((subOrder, index) => {
        mapped[`${subOrder.warehouse.id}${index}`] = subOrder.skus.map((sku) => {
          const foundMainSku = this.mainSkus.find((mainSku) => mainSku.id === sku.id)
          let discount = sku.fullPrice - sku.price

          if (foundMainSku && foundMainSku.discountPrice <= 0) {
            discount = 0
          } else if (foundMainSku) {
            discount = (foundMainSku.discountPrice / foundMainSku.amount) * sku.amount
          }

          const mainDiscount = mainSkusDiscount[sku.id]

          if (discount > mainDiscount) {
            discount = mainDiscount < 0 ? 0 : mainDiscount
          }

          mainSkusDiscount[sku.id] -= discount

          return {
            ...sku,
            discountType: 'amount',
            discountPrice: sku.fullPrice - sku.price,
            unitPrice: sku?.unitPrice || (sku.fullPrice / sku.amount),
            trackingNo: subOrder.shipping?.refCode || ''
          }
        })
      })

      mapped[0] = this.mainSkus

      return mapped
    },
    calcDiscountPerWarehouse (sku, detail) {
      const { discountType } = detail
      const discountPrice = discountType === 'amount' ? (detail.discountPrice / detail.amount) * sku.amount : detail.discountPrice

      return { ...sku, discountPrice, discountType }
    },
    openProductModal () {
      if (!this.showButton) {
        return
      }

      this.setProductModalActive(true)
    },
    imgSrcCompute (src) {
      if (src) {
        return `${src}?fm=webp&q=80&w=200`
      }

      return 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/1200px-No-Image-Placeholder.svg.png'
    },
    getPreOrder (item) {
      return item?.preOrder ?? false
    }
  }
}
</script>

<style lang="scss">
.order-product-add-btn {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: #f7f7f7;
  border: 1px dashed #898989;
  border-radius: 5px;
  color: #898989;
  font-size: 14px;
  font-weight: 500;
  margin: 10px auto 0;
  padding: 5px;
  width: 97%;
}
#order-product-table p.text-price {
  margin: 4px 0;
}
#order-product-table.order-product-empty table {
  height: 100%;
  td {
    height: 100%;
    button.v-btn {
      height: 100%;
      position: relative;
      z-index: 2;
    }
  }
}
.font-super-bold {
  font-size: 14px;
  font-weight: 800;
}
</style>
